import { memo, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useVideoObjectBlobUpdateMutation } from 'hooks/mutations/content/VideoObject'
import { videoObjectBlobConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs/Blob'
import { videoObjectQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { videoobjectId: videoId, blobId } = videoObjectBlobConfigRoute.useParams()

  const videoBlobQuery = useQuery(videoObjectQueryKeys.blobDetail({ videoId, blobId }))
  const { data: blob } = videoBlobQuery

  const { mutate } = useVideoObjectBlobUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && videoBlobQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(blob, null, 4))
    }
  }, [videoBlobQuery.isSuccess, blob])

  if (videoBlobQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!blob) {
    return <NotFound />
  }

  return (
    <Editor
      value={blob}
      onSubmit={(data) => {
        mutate(
          {
            videoId,
            blobId,
            videoBlobEdit: data,
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:Workspace.Video.videoBlobSaved'), { variant: 'success' })
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
